// ShareDialog — Modal zum Verwalten der Projekt-Freigaben (Schritt 1 Sharing).
//
// Aufbau:
//  - Owner-Zeile fix oben (read-only, Badge "Owner").
//  - Aktuelle Member-Liste: Avatar + Name + Rollen-Dropdown (Betrachter/Bearbeiter)
//    + Entfernen.
//  - "Personen hinzufügen": Suche im User-Directory (wie AssigneePicker), Klick
//    fügt mit Default-Rolle "Bearbeiter" hinzu.
//
// Alle Member-Endpoints werden über store/members.ts angesprochen (optimistisch,
// Fehler-Toasts über den api()-Wrapper). Nur Owner/Admin sehen diesen Dialog.

import { useMemo, useState } from "preact/hooks";
import { Search, UserPlus, X } from "lucide-preact";
import type {
  ProjectMemberRole,
  ProjectWithCounts,
  UserPublic,
} from "@juuplan/shared";
import { PROJECT_MEMBER_ROLES } from "@juuplan/shared";
import { Modal } from "./Modal";
import { UserAvatar } from "./UserAvatar";
import { useUsers, findUser } from "../store/users";
import {
  useMembers,
  addMember,
  updateMemberRole,
  removeMember,
} from "../store/members";
import { refreshProjectDetail } from "../store/project";
import { refreshProjects } from "../store/projects";
import { confirmDialog } from "../lib/confirm";

interface Props {
  project: ProjectWithCounts;
  onClose: () => void;
}

const ROLE_LABEL: Record<ProjectMemberRole, string> = {
  viewer: "Betrachter",
  editor: "Bearbeiter",
};

export function ShareDialog(props: Props): preact.JSX.Element {
  const { project, onClose } = props;
  const users = useUsers();
  const members = useMembers(project.id);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);

  const owner = findUser(project.owner_id);

  // Set der bereits vergebenen User-Ids (Owner + Member) — diese tauchen in der
  // Such-Liste nicht erneut auf.
  const takenIds = useMemo(() => {
    const s = new Set<number>([project.owner_id]);
    for (const m of members.items) s.add(m.user.id);
    return s;
  }, [project.owner_id, members.items]);

  const candidates: UserPublic[] = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Gaeste lehnt der Server als Member ab (projectMembers: "External guests
    // are invited via external invites") — frueher standen sie trotzdem hier,
    // und der Klick endete in einem Fehler-Toast. Sie werden in den
    // Projekt-Einstellungen unter „Externe Gaeste" eingeladen.
    return users.items
      .filter((u) => !takenIds.has(u.id) && !u.is_external)
      .filter(
        (u) =>
          !q ||
          u.name.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q)
      );
  }, [users.items, takenIds, query]);

  // Nach jeder Mutation Projekt + Liste auffrischen, damit member_count und die
  // Sidebar-Gruppierung stimmen.
  const syncProject = (): void => {
    void refreshProjectDetail();
    void refreshProjects();
  };

  const onAdd = async (userId: number): Promise<void> => {
    setBusy(true);
    try {
      await addMember(project.id, userId, "editor");
      syncProject();
    } catch {
      // Toast kommt aus api(); Liste bleibt unverändert (kein optimistisches Insert bei Fehler).
    } finally {
      setBusy(false);
    }
  };

  const onRoleChange = async (
    userId: number,
    role: ProjectMemberRole
  ): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      // api() toastet bereits bei Fehler; syncProject() liest danach ohnehin
      // den echten Serverstand, das schluckt kein Ergebnis.
      await updateMemberRole(project.id, userId, role).catch(() => undefined);
      syncProject();
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async (userId: number, name: string): Promise<void> => {
    if (busy) return;
    const ok = await confirmDialog({
      title: "Zugriff entfernen",
      message: `${name} aus dem Projekt entfernen? Die Person verliert sofort den Zugriff.`,
      confirmLabel: "Entfernen",
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      // api() toastet bereits bei Fehler; syncProject() liest danach ohnehin
      // den echten Serverstand, das schluckt kein Ergebnis.
      await removeMember(project.id, userId).catch(() => undefined);
      syncProject();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`„${project.name}" teilen`} onClose={onClose} size="md">
      <div class="flex flex-col gap-4">
        {/* Owner pinned */}
        <div>
          <h3 class="text-label font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-2">
            Personen mit Zugriff
          </h3>
          <ul class="flex flex-col gap-1">
            <li class="flex items-center gap-2.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-3 py-2">
              <UserAvatar userId={project.owner_id} size={28} />
              <div class="flex-1 min-w-0">
                <p class="text-sm text-[color:var(--color-on-surface)] truncate">
                  {owner?.name ?? `User #${project.owner_id}`}
                </p>
                {owner?.email && (
                  <p class="text-label text-[color:var(--color-on-surface-variant)] truncate">
                    {owner.email}
                  </p>
                )}
              </div>
              <span class="shrink-0 inline-flex items-center rounded-full bg-[color:var(--color-primary)]/10 px-2.5 py-1 text-label font-semibold uppercase tracking-wider text-[color:var(--color-primary-strong)]">
                Owner
              </span>
            </li>

            {members.loading ? (
              <li class="px-3 py-2 text-xs text-[color:var(--color-on-surface-variant)]">
                Lädt Mitglieder…
              </li>
            ) : (
              members.items.map((m) => (
                <li
                  key={m.user.id}
                  class="flex items-center gap-2.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2"
                >
                  <UserAvatar userId={m.user.id} size={28} name={m.user.name} avatarUrl={m.user.avatar_url} />
                  <div class="flex-1 min-w-0">
                    <p class="text-sm text-[color:var(--color-on-surface)] truncate">
                      {m.user.name}
                    </p>
                    <p class="text-label text-[color:var(--color-on-surface-variant)] truncate">
                      {m.user.email}
                    </p>
                  </div>
                  <select
                    value={m.role}
                    onChange={(e) =>
                      void onRoleChange(
                        m.user.id,
                        (e.target as HTMLSelectElement).value as ProjectMemberRole
                      )
                    }
                    class="shrink-0 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-1 min-h-[28px]"
                    aria-label={`Rolle von ${m.user.name}`}
                  >
                    {PROJECT_MEMBER_ROLES.map((r) => (
                      <option key={r} value={r}>
                        {ROLE_LABEL[r]}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void onRemove(m.user.id, m.user.name)}
                    class="shrink-0 rounded p-1.5 text-[color:var(--color-error)] hover:bg-[color:var(--color-error-container)] hover:text-[color:var(--color-on-error-container)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-error)] focus-visible:ring-offset-1 disabled:opacity-50 transition-colors"
                    aria-label={`${m.user.name} entfernen`}
                    title="Entfernen"
                  >
                    <X size={14} aria-hidden="true" />
                  </button>
                </li>
              ))
            )}
            {!members.loading && members.items.length === 0 && (
              <li class="px-3 py-2 text-xs text-[color:var(--color-on-surface-variant)] italic">
                Noch niemand freigegeben.
              </li>
            )}
          </ul>
        </div>

        {/* Add people */}
        <div>
          <h3 class="text-label font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-2">
            Personen hinzufügen
          </h3>
          <div class="inline-flex w-full items-center gap-1.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2.5 py-1.5 focus-within:ring-2 focus-within:ring-[color:var(--color-primary)] focus-within:ring-offset-1 transition-shadow">
            <Search size={14} class="text-[color:var(--color-on-surface-variant)]" aria-hidden="true" />
            <input
              type="text"
              value={query}
              onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
              placeholder="Name oder E-Mail suchen…"
              class="flex-1 bg-transparent text-base sm:text-sm text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none"
              aria-label="Personen suchen"
            />
          </div>
          <ul class="mt-2 max-h-56 overflow-y-auto rounded border border-[color:var(--color-outline-variant)] divide-y divide-[color:var(--color-outline-variant)]/50">
            {users.loading ? (
              <li class="px-3 py-2 text-xs text-[color:var(--color-on-surface-variant)]">
                Lädt Nutzer…
              </li>
            ) : candidates.length === 0 ? (
              <li class="px-3 py-2 text-xs text-[color:var(--color-on-surface-variant)] italic">
                {query.trim() ? "Keine Treffer." : "Alle verfügbaren Nutzer sind bereits hinzugefügt."}
              </li>
            ) : (
              candidates.map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void onAdd(u.id)}
                    class="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-inset disabled:opacity-60 transition-colors"
                  >
                    <UserAvatar userId={u.id} size={24} name={u.name} avatarUrl={u.avatar_url} />
                    <div class="flex-1 min-w-0">
                      <p class="text-sm text-[color:var(--color-on-surface)] truncate">{u.name}</p>
                      <p class="text-label text-[color:var(--color-on-surface-variant)] truncate">{u.email}</p>
                    </div>
                    <UserPlus size={16} class="shrink-0 text-[color:var(--color-primary)]" aria-hidden="true" />
                  </button>
                </li>
              ))
            )}
          </ul>
          <p class="mt-1.5 text-label text-[color:var(--color-on-surface-variant)]">
            Neu hinzugefügte Personen werden als <strong>Bearbeiter</strong> freigegeben. Rolle danach oben anpassbar.
          </p>
          <p class="mt-1 text-label text-[color:var(--color-on-surface-variant)]">
            Externe Gäste lädst du in den Projekt-Einstellungen unter <strong>Externe Gäste</strong> ein.
          </p>
        </div>
      </div>
    </Modal>
  );
}
